import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  DndContext,
  DragOverlay,
  useDraggable,
  useDroppable,
  useDndContext,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  updateAssignmentMetadata,
  updateAssignmentChunksAndAppendLog,
} from '../services/firebaseService';
import { useTripsAssignment, useMultiloadRoutes } from '../queries/useAssignmentChunks';
import { useAssignmentRouteEnabled } from '../hooks/useAssignmentRouteEnabled';
import TempTripFeasibilityPanel from '../components/TempTripFeasibilityPanel';
import { useTimeFilter } from '../contexts/TimeFilterContext';
import { usePublishAccess } from '../contexts/PublishAccessContext';
import useReverseGeocode from '../hooks/useReverseGeocode';
import { copyTextToClipboard } from '../utils/clipboard';
import { recomputeDeadheadAndEtaForDriver, haversineDistance, getTripCoords, normalizeTripCoords, sumDeadheadMilesForTotal } from '../utils/deadheadEta';
import { sortTripsForDisplay, buildTripSections } from '../utils/tripSections';
import { clientNameWithLeg } from '../utils/groupLeg';
import { getMultiloadGroupP1Trip } from '../utils/tripFreeTime';
import { Check, Copy, Home, Truck, Upload, X, ArrowLeft, UserPlus, ChevronDown } from 'lucide-react';
import { auth } from '../firebase';
import { schedulerApiFetch } from '../api/schedulerClient';
import { PickupEstimateMeta, DropoffEstimateMeta } from '../components/TripEstimateInline';
import TripFreeTimeBanner from '../components/TripFreeTimeBanner';
import {
  buildAssignLinesOrderPayload,
  buildPlannerSingleDriverPayload,
  filterPayloadByValidatedTripIds,
  hasPastDatedPlanTrips,
  reprioritizePublishPayloadData,
  resolvePlannerDriverIdForPortal,
} from '../utils/portalPlanPublish';
import { RiRestTimeFill } from 'react-icons/ri';
import { tripMatchesTripsPageSearch, searchTokensFromInput, driverNameMatchesSearch } from '../utils/tripPageSearch';
import CompanyNoteIconButton, { getCompanyNote } from '../components/CompanyNoteIconButton';
import TripRevenueLabel from '../components/TripRevenueLabel';
import { stampManualAddRevenue, sumTripRevenue } from '../utils/tripRevenue';
import { DriverFreezeTypeBadge } from '../components/DriverFreezeTypeBadge';
import DriverCardIdentity from '../components/DriverCardIdentity';
import FleetWindowHeader from '../components/FleetWindowHeader';
import {
  applyWindowIndex,
  isFleetDriver,
  isFleetSession,
  lastWindowAwayMiles,
  splitDriverForEstimate,
  stitchEstimates,
  windowForTime,
} from '../utils/fleetWindows';

// --- Helpers ---
function reindexSequences(trips) {
  if (!trips?.length) return trips;
  let seq = 0;
  for (const t of trips) {
    if (t.is_break) {
      if (t.sequence == null) t.sequence = seq;
    } else {
      seq += 1;
      t.sequence = seq;
    }
  }
  return trips;
}

/** Recompute miles, trip count, and revenue from each driver's trips so a refresh matches the cards. */
function recomputeDriverTotals(drivers) {
  if (!drivers?.length) return drivers || [];
  return drivers.map((d) => {
    const trips = d.trips || [];
    const total_distance_miles = trips.reduce(
      (sum, t) => sum + (Number(t.trip_distance_miles) || Number(t.trip_distance) || 0),
      0
    );
    const total_trips = trips.filter((t) => !t.is_break).length;
    return {
      ...d,
      total_distance_miles: Math.round(total_distance_miles * 100) / 100,
      total_trips,
      total_revenue: sumTripRevenue(trips),
    };
  });
}

function retagDriverWindows(driver) {
  if (!isFleetDriver(driver)) return false;
  let gap = false;
  driver.trips = (driver.trips || []).map((trip) => {
    if (trip?.is_break) return trip;
    const tagged = applyWindowIndex(driver, trip);
    if (tagged.fleet_window_index == null) gap = true;
    return tagged;
  });
  return gap;
}

function recomputeDeadheadMaybeFleet(driver, routes, assignmentData) {
  if (isFleetSession(assignmentData) && isFleetDriver(driver)) {
    const parts = splitDriverForEstimate(driver);
    for (const part of parts) recomputeDeadheadAndEtaForDriver(part, routes);
    const stitched = stitchEstimates(driver, parts);
    driver.trips = stitched.trips;
    return;
  }
  recomputeDeadheadAndEtaForDriver(driver, routes);
}

/** Strip multiload fields so the trip is treated as a single trip (no group border/route). Used when a multiload group is moved to unassigned. */
function stripMultiloadFields(trip) {
  const out = { ...trip };
  delete out.multiload_group_id;
  delete out.multiload_pickup_order;
  delete out.multiload_partner_ids;
  out.is_multiload = false;
  if (out.hasOwnProperty('is_multiload_primary')) out.is_multiload_primary = false;
  return out;
}

function formatTime(isoString, timezone = 'America/Los_Angeles') {
  if (!isoString) return '–';
  try {
    return new Date(isoString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: timezone,
    });
  } catch {
    return isoString;
  }
}

// Format break time - handles both pre-formatted strings (e.g. "10:00 AM") and ISO strings
function formatBreakTime(timeStr, timezone = 'America/Los_Angeles') {
  if (!timeStr) return '–';
  const str = String(timeStr);
  if (str.indexOf('T') === -1) return str;
  try {
    return new Date(timeStr).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: timezone,
    });
  } catch {
    return str;
  }
}

/** MM/DD/YYYY HH:mm in company timezone (matches Trips page portal-style display). */
function formatShortDateTime(isoString, timezone = 'America/Los_Angeles') {
  if (!isoString) return '';
  const tz = timezone || 'America/Los_Angeles';
  try {
    const date = new Date(isoString);
    const datePart = date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      timeZone: tz,
    });
    const timePart = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: tz,
    });
    return `${datePart} ${timePart}`;
  } catch {
    return '';
  }
}

function formatShiftTime(isoString, timezone = 'America/Los_Angeles') {
  if (!isoString) return '';
  const tz = timezone || 'America/Los_Angeles';
  try {
    return new Date(isoString).toLocaleString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: tz,
    });
  } catch {
    return '';
  }
}

/** Trip id line like Trips / reference: `MFR90796 (LEG-A)` when leg metadata exists. */
function formatTripIdSubtitleLine(trip) {
  const id = trip.trip_id || trip.id || '';
  if (trip.leg_label || trip.leg_name) {
    const leg = String(trip.leg_label || trip.leg_name).trim();
    if (!id) return leg || '–';
    if (leg.startsWith('(')) return `${id} ${leg}`;
    return `${id} (${leg})`;
  }
  if (trip.is_group_trip && trip.group_index != null && Number(trip.group_index) > 0) {
    const gi = Math.min(26, Math.max(1, Number(trip.group_index)));
    const letter = String.fromCharCode(64 + gi);
    return id ? `${id} (LEG-${letter})` : `(LEG-${letter})`;
  }
  return id || '–';
}

function getSpaceTypeLabel(trip) {
  if (trip.losTitle && trip.spaceTypeTitle) return `${trip.losTitle} ( ${trip.spaceTypeTitle} )`;
  if (trip.los_title && trip.space_type_title) return `${trip.los_title} ( ${trip.space_type_title} )`;
  return trip.space_type || trip.spaceTypeTitle || trip.losTitle || '–';
}

// --- Driver column header (Trips-style layout; same data + freeze badges as before) ---
function DriverColumnHeader({ driver, timezone, onExportLoad, exportDisabled, exportWorking }) {
  const tz = timezone || 'America/Los_Angeles';
  const currentLat = driver.current_latitude || driver.driver_latitude || driver.latitude;
  const currentLng = driver.current_longitude || driver.driver_longitude || driver.longitude;
  const hasCurrentLocation = currentLat != null && currentLng != null;
  const currentLocationText = hasCurrentLocation ? `${currentLat}, ${currentLng}` : '';

  const refLat = driver.ref_latitude;
  const refLng = driver.ref_longitude;
  const hasRefLocation = refLat != null && refLng != null;
  const refLocationText = hasRefLocation ? `${refLat}, ${refLng}` : '';

  const [copiedCurrentAddress, setCopiedCurrentAddress] = useState(false);
  const [copiedCurrentCoords, setCopiedCurrentCoords] = useState(false);
  const [copiedRefAddress, setCopiedRefAddress] = useState(false);
  const [copiedRefCoords, setCopiedRefCoords] = useState(false);
  const [locationsOpen, setLocationsOpen] = useState(false);

  const { address: currentAddress, loading: currentAddressLoading } = useReverseGeocode(currentLat, currentLng);
  const { address: refAddress, loading: refAddressLoading } = useReverseGeocode(refLat, refLng);

  const copyToClipboard = async (text, setter) => {
    if (!text) return;
    const ok = await copyTextToClipboard(text);
    if (ok) {
      setter(true);
      setTimeout(() => setter(false), 2000);
    }
  };

  const shiftStart = driver.shift_window?.shift_start;
  const shiftEnd = driver.shift_window?.shift_end;
  const legacyShiftStr =
    shiftStart && shiftEnd ? `${formatTime(shiftStart, tz)} - ${formatTime(shiftEnd, tz)}` : null;

  // Away from Home (unchanged math vs previous bucket header)
  const fleetMode = isFleetDriver(driver);
  const fleetCount = (driver.fleet_windows || []).length;
  const shiftLabel = driver.shift_window?.has_shift && shiftStart && shiftEnd
    ? `${formatShiftTime(shiftStart, tz)}—${formatShiftTime(shiftEnd, tz)}`
    : (legacyShiftStr || '—');
  const awayFromHomeMiles = fleetMode
    ? lastWindowAwayMiles(driver)
    : (() => {
    const curLat = driver.current_latitude;
    const curLng = driver.current_longitude;
    const rLat = driver.ref_latitude ?? driver.current_latitude;
    const rLng = driver.ref_longitude ?? driver.current_longitude;
    const trips = driver.trips || [];
    if (!trips.length) return 0;
    const sorted = [...trips].sort((a, b) => (a.sequence ?? 999) - (b.sequence ?? 999));
    const lastTrip = sorted[sorted.length - 1];
    if (!lastTrip || lastTrip.is_break) return 0;
    const coords = getTripCoords(lastTrip);
    if (!coords) return 0;
    let homeLat; let homeLng;
    if (driver.takeMeHome && Array.isArray(driver.takeMeHome) && driver.takeMeHome.length >= 2) {
      homeLng = driver.takeMeHome[0];
      homeLat = driver.takeMeHome[1];
    } else {
      homeLat = rLat ?? curLat;
      homeLng = rLng ?? curLng;
    }
    if (homeLat == null || homeLng == null) return 0;
    return haversineDistance(coords.dropoffLat, coords.dropoffLng, homeLat, homeLng);
  })();

  const tripCountNonBreak =
    driver.trips?.filter((t) => !t.is_break).length ?? driver.total_trips ?? 0;
  const totalMiles =
    driver.trips?.reduce(
      (sum, t) => sum + (Number(t.trip_distance_miles) || Number(t.trip_distance) || 0),
      0
    ) ?? driver.total_distance_miles ?? 0;
  const totalDeadhead = sumDeadheadMilesForTotal(driver.trips);
  const totalRevenue = sumTripRevenue(driver.trips);

  return (
    <div className="flex-shrink-0 bg-main-primary text-white p-2 rounded-t-md space-y-2 select-none shadow-lg border-b border-white/10">
      <DriverCardIdentity
        driver={driver}
        fleetMode={fleetMode}
        shiftLabel={shiftLabel}
        timezone={tz}
        trailing={onExportLoad ? (
          <button
            type="button"
            onPointerDown={(e) => {
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.stopPropagation();
              onExportLoad();
            }}
            disabled={exportDisabled || exportWorking}
            title={
              exportDisabled
                ? 'No trips to publish, or a trip is in the past for this load'
                : 'Publish this driver'
            }
            className="relative z-20 inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded border border-white/50 bg-white/20 text-white hover:bg-white/30 active:bg-white/25 disabled:cursor-not-allowed disabled:opacity-45 transition-colors"
            aria-label="Publish this driver"
          >
            {exportWorking ? (
              <span className="h-2.5 w-2.5 animate-spin rounded-full border border-white/30 border-t-white" />
            ) : (
              <Upload className="h-2.5 w-2.5" strokeWidth={2.5} />
            )}
          </button>
        ) : null}
      />

      <div className="overflow-hidden rounded-md border border-white/5 bg-white/5 text-center">
        <div className="grid grid-cols-4 items-center py-1">
          <div className="flex items-baseline justify-center gap-0.5 whitespace-nowrap border-r border-white/5 px-1.5" title="Loaded Miles">
            <span className="text-[7px] font-black uppercase leading-none tracking-tighter opacity-60">LM:</span>
            <span className="text-[10px] font-black leading-none tabular-nums">{Number(totalMiles).toFixed(1)}mi</span>
          </div>
          <div className="flex items-baseline justify-center gap-0.5 whitespace-nowrap border-r border-white/5 px-1.5">
            <span className="text-[7px] font-black uppercase leading-none tracking-tighter opacity-60">DH:</span>
            <span className="text-[10px] font-black leading-none tabular-nums text-yellow-500">{Number(totalDeadhead).toFixed(1)}mi</span>
          </div>
          <div className="flex items-baseline justify-center gap-0.5 whitespace-nowrap border-r border-white/5 px-1.5">
            {awayFromHomeMiles > 0 ? (
              <>
                <span className="text-[7px] font-black uppercase leading-none tracking-tighter opacity-60">Away:</span>
                <span className="inline-flex items-baseline gap-0.5 text-[10px] font-black leading-none tabular-nums text-blue-300">
                  <Home className="h-2 w-2 shrink-0 self-center" />
                  {awayFromHomeMiles.toFixed(1)}mi
                </span>
              </>
            ) : (
              <span className="text-[10px] font-black leading-none opacity-10">—</span>
            )}
          </div>
          <div className="flex items-baseline justify-center gap-0.5 whitespace-nowrap px-1.5">
            <span className="text-[7px] font-black uppercase leading-none tracking-tighter opacity-60">Total:</span>
            <span className="text-[10px] font-black leading-none tabular-nums">
              {(Number(totalMiles) + Number(totalDeadhead) + (awayFromHomeMiles || 0)).toFixed(1)}mi
            </span>
          </div>
        </div>
        <TripRevenueLabel
          value={totalRevenue}
          variant="header"
          middleLabel={fleetMode ? 'Fleet' : ''}
          middleValue={fleetMode ? (fleetCount || '—') : ''}
          tripCount={tripCountNonBreak}
        />
      </div>

      {(hasCurrentLocation || hasRefLocation || driver.home_address) && (
        <div className="mt-1">
          <button
            type="button"
            aria-expanded={locationsOpen}
            aria-label={locationsOpen ? 'Hide current GPS and starting point' : 'Show current GPS and starting point'}
            onClick={(e) => {
              e.stopPropagation();
              setLocationsOpen((open) => !open);
            }}
            className="flex w-full items-center gap-1 rounded px-0.5 py-0.5 text-left hover:bg-white/10 transition-colors"
          >
            <ChevronDown
              className={`h-3 w-3 shrink-0 opacity-80 transition-transform ${locationsOpen ? 'rotate-180' : ''}`}
              aria-hidden="true"
            />
            <span className="text-[6.5px] font-black uppercase tracking-[0.1em] leading-none opacity-70">
              Current GPS / Starting Point
            </span>
          </button>
          {locationsOpen && (
            <div className="space-y-1 mt-1">
              {hasCurrentLocation && (
                <div className="space-y-0.5 group">
                  <div className="text-[6.5px] font-black uppercase opacity-60 tracking-[0.1em] leading-none mb-1">Current GPS:</div>
                  <div className="flex items-start justify-between gap-1 pl-0.5">
                    <div
                      className="text-[8.5px] opacity-90 font-bold leading-tight flex-1 pr-4 min-w-0"
                      title={currentAddress || currentLocationText}
                    >
                      {currentAddress || 'Locating position...'}
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        copyToClipboard(currentAddress || currentLocationText, setCopiedCurrentAddress);
                      }}
                      className="p-0.5 hover:bg-white/10 rounded transition-all opacity-0 group-hover:opacity-100 min-w-[18px]"
                    >
                      {copiedCurrentAddress ? <Check className="w-2 h-2 text-emerald-400" /> : <Copy className="w-2 h-2" />}
                    </button>
                  </div>
                  <div className="flex items-center justify-between gap-1 pl-0.5">
                    <span className="font-mono text-[8.5px] font-medium opacity-60 tracking-tighter leading-none truncate">
                      {Number(currentLat).toFixed(6)}, {Number(currentLng).toFixed(6)}
                    </span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        copyToClipboard(currentLocationText, setCopiedCurrentCoords);
                      }}
                      className="p-0.5 hover:bg-white/10 rounded transition-all opacity-0 group-hover:opacity-100 min-w-[18px]"
                    >
                      {copiedCurrentCoords ? <Check className="w-2 h-2 text-emerald-400" /> : <Copy className="w-2 h-2" />}
                    </button>
                  </div>
                  <div className="text-[8px] text-white/50 pl-0.5">
                    {currentAddressLoading ? 'Loading address…' : null}
                  </div>
                </div>
              )}

              {(hasRefLocation || driver.home_address) && (
                <div className={`space-y-0.5 group ${hasCurrentLocation ? 'border-t border-white/5 pt-1.5 mt-1' : ''}`}>
                  <div className="text-[6.5px] font-black uppercase opacity-60 tracking-[0.1em] text-yellow-100 leading-none mb-1">
                    Starting Point:
                  </div>
                  <div className="flex items-start justify-between gap-1 pl-0.5">
                    <div
                      className="text-[8.5px] opacity-90 font-bold leading-tight flex-1 text-yellow-100/90 pr-4 min-w-0"
                      title={refAddress || refLocationText || driver.home_address}
                    >
                      {refAddress || driver.home_address || 'Reference address...'}
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        copyToClipboard(refAddress || refLocationText || driver.home_address, setCopiedRefAddress);
                      }}
                      className="p-0.5 hover:bg-white/10 rounded transition-all opacity-0 group-hover:opacity-100 min-w-[18px]"
                    >
                      {copiedRefAddress ? <Check className="w-2 h-2 text-emerald-400" /> : <Copy className="w-2 h-2" />}
                    </button>
                  </div>
                  {hasRefLocation && (
                    <div className="flex items-center justify-between gap-1 pl-0.5">
                      <span className="font-mono text-[8.5px] font-medium opacity-60 tracking-tighter leading-none text-yellow-100/70 truncate">
                        {Number(refLat).toFixed(6)}, {Number(refLng).toFixed(6)}
                      </span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(refLocationText, setCopiedRefCoords);
                        }}
                        className="p-0.5 hover:bg-white/10 rounded transition-all opacity-0 group-hover:opacity-100 min-w-[18px]"
                      >
                        {copiedRefCoords ? <Check className="w-2 h-2 text-emerald-400" /> : <Copy className="w-2 h-2" />}
                      </button>
                    </div>
                  )}
                  <div className="text-[8px] text-yellow-100/50 pl-0.5">
                    {refAddressLoading ? 'Loading address…' : null}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// --- Detailed trip card content (assigned: with ETA/deadhead/distance; unassigned: without, no reason) ---
function TripCardContent({ trip, timezone, isUnassigned, spaceTypeMap, compact = false, driverTrips = null, multiloadRoutes = null }) {
  const [copiedField, setCopiedField] = useState(null);
  const isBreak = trip.is_break === true;
  const manuallyAdjusted = trip.manually_adjusted === true;

  const copy = async (text, field) => {
    if (!text) return;
    const ok = await copyTextToClipboard(text);
    if (ok) {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    }
  };

  if (isBreak) {
    const breakDuration = trip.break_duration_minutes || 0;
    const breakStartDisplay = formatBreakTime(trip.break_start || trip.schedule_time, timezone);
    const breakEndDisplay = formatBreakTime(trip.break_end || trip.appointment_time, timezone);
    const breakText = compact ? 'text-[10px]' : 'text-xs'; // compact only affects break cards in drag overlay
    return (
      <>
        <TripFreeTimeBanner trip={trip} driverTrips={driverTrips} timezone={timezone || 'America/Los_Angeles'} multiloadRoutes={multiloadRoutes} />
        <div className={`border border-dashed border-main-primary rounded-lg p-2 bg-teal-50/60 transition-all hover:shadow-sm ${breakText} text-main-secondary`}>
          <div className="flex justify-between items-start mb-2">
            <div className="flex items-center gap-2 min-w-0">
              <div className="h-7 w-7 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                <RiRestTimeFill className="text-teal-600 text-sm" />
              </div>
              <div className="min-w-0 flex items-center gap-2">
                <h4 className="font-bold text-main-dark leading-none truncate">
                  {clientNameWithLeg(trip.client_name || 'Rest Break', trip)}
                </h4>
                <span className="text-[8px] bg-main-primary text-white px-1.5 py-0.5 rounded font-black uppercase tracking-wider flex-shrink-0">
                  Break
                </span>
              </div>
            </div>
            <span className="text-[10px] px-2 py-0.5 rounded bg-teal-100 text-teal-700 font-black shrink-0">
              #{trip.sequence ?? '–'}
            </span>
          </div>
          <div className="grid grid-cols-3 px-1 py-1.5 border border-teal-200/50 bg-white/50 rounded-md text-center items-center">
            <div className="flex items-center justify-center gap-1 border-r border-teal-200/30">
              <span className="opacity-60 text-[7px] font-black uppercase tracking-tighter text-teal-800">Duration:</span>
              <span className="text-[10px] font-black text-teal-900">{Math.round(breakDuration)}m</span>
            </div>
            <div className="flex items-center justify-center gap-1 border-r border-teal-200/30">
              <span className="opacity-60 text-[7px] font-black uppercase tracking-tighter text-teal-800">Start:</span>
              <span className="text-[10px] font-black text-teal-900">{breakStartDisplay}</span>
            </div>
            <div className="flex items-center justify-center gap-1">
              <span className="opacity-60 text-[7px] font-black uppercase tracking-tighter text-teal-800">End:</span>
              <span className="text-[10px] font-black text-teal-900">{breakEndDisplay}</span>
            </div>
          </div>
        </div>
      </>
    );
  }

  const tz = timezone || 'America/Los_Angeles';
  // Same busy / status color scheme as Trips `/` TripCard (`TripsPage.js` TripCard).
  const isBusyTrip = trip.is_busy_trip === true;
  const tripStatus = (trip.trip_status || '').toLowerCase();
  const busyStyles = (() => {
    switch (tripStatus) {
      case 'onway':
      case 'on way':
        return {
          card: 'border-blue-200 bg-blue-50/50',
          seq: 'bg-blue-100 text-blue-700',
          badge: 'bg-blue-500',
          divider: 'border-blue-100',
        };
      case 'arrived':
        return {
          card: 'border-orange-200 bg-orange-50/50',
          seq: 'bg-orange-100 text-orange-700',
          badge: 'bg-orange-500',
          divider: 'border-orange-100',
        };
      case 'started':
      case 'pickedup':
        return {
          card: 'border-emerald-200 bg-emerald-50/50',
          seq: 'bg-emerald-100 text-emerald-700',
          badge: 'bg-emerald-500',
          divider: 'border-emerald-100',
        };
      default:
        return isBusyTrip
          ? {
            card: 'border-sky-200 bg-sky-50/50',
            seq: 'bg-sky-100 text-sky-700',
            badge: 'bg-sky-500',
            divider: 'border-sky-100',
          }
          : {};
    }
  })();

  const isBusyForBanner = trip.is_busy_trip === true || trip.sequence === 0;
  const deadhead = trip.deadhead_miles ?? 0;
  const eta = isBusyForBanner ? trip.available_in_minutes : trip.eta_to_pickup_minutes;
  const showBanner = deadhead > 0 || (eta !== undefined && eta !== null);

  const isMultiload = trip.is_multiload === true;
  const multiloadGroupSize = isMultiload
    ? (trip.multiload_partner_ids?.length || 0) + 1
    : 0;
  const multiloadPosition = isMultiload
    ? (trip.multiload_pickup_order ?? (trip.multiload_partner_ids ? trip.multiload_partner_ids.filter((id) => id < trip.trip_id).length + 1 : 0))
    : 0;
  const showMultiloadBadge = isMultiload && multiloadGroupSize > 1;
  const escortCount = Number(trip.no_of_escorts ?? trip.noOfEscorts ?? 0) || 0;
  const hasEscorts = escortCount > 0;

  const spaceTypeDisplay = (() => {
    if (trip.losTitle && trip.spaceTypeTitle) return { los: trip.losTitle, space: trip.spaceTypeTitle };
    if (trip.los_title && trip.space_type_title) return { los: trip.los_title, space: trip.space_type_title };
    const stData = spaceTypeMap?.[trip.space_type || trip.space_type_id];
    if (typeof stData === 'string') return { los: stData, space: stData };
    if (stData) {
      const los = stData.los_title || stData.losTitle || stData.title;
      const space = stData.st_title || stData.space_type_title || stData.original_title || stData.title;
      return { los: los || '', space: space || los || '' };
    }
    return { los: trip.space_type || '', space: getSpaceTypeLabel(trip) };
  })();

  const tripIdLine = formatTripIdSubtitleLine(trip);

  const outerCardClasses = busyStyles?.card ? busyStyles.card : 'bg-main-base-50';

  return (
    <div
      className={`rounded-md ring-1 ring-main-base flex flex-col flex-shrink-0 text-xs text-main-secondary shadow relative transition-all overflow-hidden ${outerCardClasses}`}
      data-trip-id={trip.trip_id || trip.id}
    >
      {/* Free time on singles/breaks; multiload group shows it on Route header */}
      {!trip.is_multiload && (
        <TripFreeTimeBanner trip={trip} driverTrips={driverTrips} timezone={tz} multiloadRoutes={multiloadRoutes} />
      )}

      {showBanner && (
        <div className={`flex items-center text-xs p-2 justify-center text-center text-white ${busyStyles?.badge || 'bg-main-primary'}`}>
          <div className="flex-1">{(deadhead || 0).toFixed(2)} miles</div>
          <div className="w-8">
            <svg className="h-6 w-6 mx-auto" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M19.5 13.5 12 21m0 0-7.5-7.5M12 21V3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="flex-1">{eta != null ? `${Math.round(eta)} mins` : 'N/A'}</div>
        </div>
      )}

      <div className="p-2">
        <div className="flex mt-2 items-start justify-between flex-wrap gap-1">
          <div className="flex flex-col min-w-0">
            <div className="text-main-primary font-semibold truncate" title={clientNameWithLeg(trip.client_name || '', trip)}>
              {clientNameWithLeg(trip.client_name || '–', trip)}
            </div>
            <div className="text-main-light    truncate" title={tripIdLine}>
              {tripIdLine}
            </div>
          </div>
          <div className="flex items-center gap-1 flex-wrap justify-end shrink-0 max-w-[58%]">
            {trip.preferred_driver_info?.preferred_driver_name && (
              <span className="inline-flex items-center rounded-md bg-emerald-600 px-1.5 py-0.5 text-[9px] font-medium text-white" title="Client's preferred driver">
                Pref: {trip.preferred_driver_info.preferred_driver_name}
              </span>
            )}
            {(trip.driver_violation === true || trip.preferred_driver_violation === true) && (
              <span className="inline-flex items-center rounded-md bg-rose-600 px-1.5 py-0.5 text-[9px] font-medium text-white" title="Assignment violates preference or constraints">
                Driver Violation
              </span>
            )}
            {isUnassigned && trip.driver_violation === true && trip.preferred_driver_info && (
              <span className="inline-flex items-center rounded-md bg-rose-600 px-1.5 py-0.5 text-[9px] font-medium text-white" title="Force preferred driver - unassigned">
                Pref unassigned
              </span>
            )}
            {trip.account_type && (
              <span className="inline-flex items-center rounded-md bg-main-dark px-1.5 py-0.5 text-[9px] font-medium text-white">
                {trip.account_type}
              </span>
            )}
            {hasEscorts && (
              <span className="inline-flex items-center gap-0.5 rounded-md bg-purple-600 px-1.5 py-0.5 text-[9px] font-black text-white" title={`${escortCount} escort${escortCount !== 1 ? 's' : ''}`}>
                <UserPlus className="h-2.5 w-2.5" strokeWidth={2.5} />
                {escortCount}
              </span>
            )}
            {busyStyles?.badge && tripStatus && (
              <span className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[9px] font-black text-white uppercase tracking-tighter ${busyStyles.badge}`}>
                {tripStatus === 'on way' || tripStatus === 'onway' ? 'On Way' : tripStatus.toUpperCase()}
              </span>
            )}
            {showMultiloadBadge && (
              <span className="inline-flex items-center rounded-md bg-main-primary px-1.5 py-0.5 text-[9px] font-medium text-white" title="Multiload position">
                {multiloadPosition}/{multiloadGroupSize}
              </span>
            )}
            {trip.new_assignment === true && (
              <span className="inline-flex items-center rounded-md bg-violet-600 px-1.5 py-0.5 text-[9px] font-black text-white uppercase tracking-tighter" title="Newly assigned in this run">
                New
              </span>
            )}
            {(trip.assumedWillCallTime === true || trip.assumed_will_call_time === true) && (
              <span className="inline-flex items-center rounded-md bg-sky-600 px-1.5 py-0.5 text-[9px] font-black text-white uppercase tracking-tighter" title="Will-call time assumed from historical pattern">
                WC
              </span>
            )}
            {!isUnassigned && (
              <span className="inline-flex items-center rounded-md bg-yellow-700 px-1.5 py-0.5 text-[9px] font-medium text-white" title="Pickup sequence">
                P({trip.sequence ?? '–'})
              </span>
            )}
            {isUnassigned && <CompanyNoteIconButton companyNote={getCompanyNote(trip)} compact />}
          </div>
        </div>

        <div className="mt-2 grid grid-cols-[1.25rem_minmax(0,1fr)] gap-x-2">
          {/* Pickup pin — rail from pin center to bottom of row */}
          <div className="relative flex h-full items-start justify-center">
            <span
              aria-hidden="true"
              className="pointer-events-none absolute left-1/2 top-2.5 bottom-0 w-0 -translate-x-1/2 border-l border-dashed border-main-light"
            />
            <div className="relative z-[1] h-5 w-5 bg-container-base rounded-full overflow-hidden grid place-content-center flex-shrink-0">
              <svg className="w-4 h-4 text-main-success" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          </div>
          <div className="min-w-0 pr-6 group/addr relative">
            <div className="text-main-secondary truncate" title={trip.pickup_address}>{trip.pickup_address || 'N/A'}</div>
            <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2">
              <div className="min-w-0 truncate tabular-nums text-main-light">{formatShortDateTime(trip.schedule_time, tz)}</div>
              {!isUnassigned && <PickupEstimateMeta trip={trip} timezone={tz} />}
            </div>
            {trip.pickup_address && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); copy(trip.pickup_address, 'pickup'); }}
                className="absolute right-0 top-0 p-1 opacity-0 group-hover/addr:opacity-100 transition-opacity bg-white/80 rounded shadow-sm"
                title="Copy pickup address"
              >
                {copiedField === 'pickup' ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3 text-main-light" />}
              </button>
            )}
          </div>
          {/* Dropoff pin — rail only to pin center (never past the pin) */}
          <div className="relative flex h-full items-start justify-center">
            <span
              aria-hidden="true"
              className="pointer-events-none absolute left-1/2 top-0 h-2.5 w-0 -translate-x-1/2 border-l border-dashed border-main-light"
            />
            <div className="relative z-[1] h-5 w-5 bg-container-base rounded-full overflow-hidden grid place-content-center flex-shrink-0">
              <svg className="w-4 h-4 text-main-danger" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          </div>
          <div className="min-w-0 pr-6 group/addr relative">
            <div className="text-main-secondary truncate" title={trip.dropoff_address}>{trip.dropoff_address || 'N/A'}</div>
            <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2">
              <div className="min-w-0 truncate tabular-nums text-main-light">{formatShortDateTime(trip.appointment_time, tz)}</div>
              {!isUnassigned && <DropoffEstimateMeta trip={trip} timezone={tz} />}
            </div>
            {trip.dropoff_address && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); copy(trip.dropoff_address, 'dropoff'); }}
                className="absolute right-0 top-0 p-1 opacity-0 group-hover/addr:opacity-100 transition-opacity bg-white/80 rounded shadow-sm"
                title="Copy dropoff address"
              >
                {copiedField === 'dropoff' ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3 text-main-light" />}
              </button>
            )}
          </div>
        </div>

        <div className="mt-2 flex flex-col gap-2">
          <div
            className={`grid items-center gap-x-2 gap-y-1 ${
              isUnassigned ? 'grid-cols-3' : 'grid-cols-4'
            }`}
          >
            <div
              className="flex min-w-0 items-center gap-x-1.5"
              title={`${spaceTypeDisplay.los || 'N/A'} (${spaceTypeDisplay.space || '...'})`}
            >
              <svg className="h-4 w-4 text-main-light flex-shrink-0" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01l-1.97 5.67c-.07.21-.11.43-.11.66v7.16c0 .83.67 1.5 1.5 1.5S6 20.33 6 19.5V19h12v.5c0 .82.67 1.5 1.5 1.5c.82 0 1.5-.67 1.5-1.5v-7.16c0-.22-.04-.45-.11-.66l-1.97-5.67zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5s1.5.67 1.5 1.5s-.67 1.5-1.5 1.5zM5 11l1.27-3.82c.14-.4.52-.68.95-.68h9.56c.43 0 .81.28.95.68L19 11H5z" fill="currentColor" /></svg>
              <div className="min-w-0 truncate text-main-light leading-none">
                <span className="text-main-dark">{spaceTypeDisplay.los || 'N/A'}</span> ({spaceTypeDisplay.space || '...'})
              </div>
            </div>
            <div
              className="flex min-w-0 items-center gap-x-1.5"
              title={`Miles: ${trip.trip_distance_miles != null ? Number(trip.trip_distance_miles).toFixed(2) : '0'} mi`}
            >
              <svg className="h-4 w-4 text-main-light flex-shrink-0" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg"><path d="M511.984 36.128C230.016 36.128.639 265.536.639 547.504c0 177.152 89.68 339.185 239.903 433.408c14.944 9.472 34.688 4.88 44.097-10.096s4.88-34.72-10.096-44.095c-54.096-33.952-99.04-78.048-133.424-128.88l33.552-19.376c15.311-8.848 20.56-28.4 11.712-43.711c-8.88-15.344-28.464-20.56-43.712-11.712l-33.6 19.391c-24.4-50.511-39.297-105.792-43.281-163.424h35.616c17.68 0 32-14.32 32-32s-14.32-32-32-32H65.95c4.24-58.687 19.776-114.304 44.56-164.592l32.16 18.56a31.745 31.745 0 0 0 15.97 4.288c11.055 0 21.807-5.744 27.743-16c8.847-15.312 3.6-34.88-11.712-43.713l-31.84-18.368c32.112-46.832 72.864-87.296 119.984-119.023l18.016 31.2c5.935 10.288 16.687 16 27.743 16a31.75 31.75 0 0 0 15.969-4.288c15.311-8.848 20.56-28.4 11.712-43.712l-17.953-31.072c49.329-23.792 103.68-38.656 160.976-42.816v39.872c0 17.68 14.32 32 32 32s32-14.32 32-32v-40c58.592 4.08 114.128 19.391 164.384 43.95l-17.36 30.049c-8.848 15.311-3.6 34.88 11.712 43.712a31.745 31.745 0 0 0 15.969 4.288c11.055 0 21.807-5.712 27.743-16l17.28-29.936a451.19 451.19 0 0 1 118.88 118.816l-29.968 17.312c-15.311 8.847-20.56 28.4-11.711 43.71c5.935 10.289 16.687 16 27.743 16c5.44 0 10.944-1.375 15.969-4.287l30.127-17.392C938.638 401.839 954 457.39 958.094 516H922.96c-17.68 0-32 14.32-32 32s14.32 32 32 32h35.12c-4.048 56.88-18.592 111.439-42.496 161.312l-31.68-18.288c-15.28-8.848-34.912-3.568-43.712 11.713c-8.848 15.311-3.6 34.88 11.712 43.712L883.68 796.8c-35.103 52.24-81.44 97.393-137.359 131.824c-15.055 9.28-19.712 29.008-10.464 44.032c6.065 9.808 16.529 15.216 27.28 15.216a31.896 31.896 0 0 0 16.753-4.752c152.464-93.904 243.472-256.784 243.472-435.632c0-281.952-229.408-511.36-511.376-511.36zm236.127 411.6c15.296-8.848 20.544-28.398 11.712-43.71c-8.832-15.296-28.416-20.544-43.712-11.696L542.287 492.674c-9.28-5.248-19.856-8.496-31.28-8.496c-35.28 0-63.84 28.591-63.84 63.807c0 35.248 28.576 63.84 63.84 63.84c35.28 0 63.84-28.592 63.84-63.84c0-.064-.016-.144-.016-.209z" fill="currentColor" /></svg>
              <div className="min-w-0 text-main-light leading-none">
                {trip.trip_distance_miles != null ? `${Number(trip.trip_distance_miles).toFixed(2)} mi` : '0 mi'}
              </div>
            </div>
            <TripRevenueLabel value={trip} />
            {!isUnassigned && (
              <div className="flex min-w-0 items-center gap-x-1.5">
                <CompanyNoteIconButton companyNote={getCompanyNote(trip)} />
              </div>
            )}
          </div>
        </div>

        {manuallyAdjusted && (
          <div className="flex items-center gap-1 pt-2 text-amber-600">
            <svg className="h-3 w-3 shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span className="font-medium">Manually adjusted</span>
          </div>
        )}
      </div>
    </div>
  );
}

// --- Draggable wrapper for a trip card ---
function DraggableTripCard({ id, trip, timezone, isUnassigned, spaceTypeMap, driverTrips = null, multiloadRoutes = null }) {
  const isBreak = trip?.is_break === true;
  const isBusyTrip = trip?.is_busy_trip === true || trip?.sequence === 0;
  const notDraggable = isBreak || isBusyTrip; // Breaks and In Progress trips cannot be moved
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id,
    disabled: notDraggable,
  });

  return (
    <div
      ref={setNodeRef}
      {...(notDraggable ? {} : { ...listeners, ...attributes })}
      className={isDragging ? 'opacity-40' : ''}
    >
      <div className={notDraggable ? 'cursor-not-allowed' : 'cursor-grab active:cursor-grabbing touch-none'}>
        <TripCardContent
          trip={trip}
          timezone={timezone}
          isUnassigned={isUnassigned}
          spaceTypeMap={spaceTypeMap}
          compact
          driverTrips={driverTrips}
          multiloadRoutes={multiloadRoutes}
        />
      </div>
    </div>
  );
}

// --- Draggable multiload group (whole group moves as one; individual trips inside are not draggable) ---
function DraggableMultiloadGroup({ id, section, routeStr, driver, timezone, spaceTypeMap, multiloadRoutes = null }) {
  const trips = section.trips || [];
  const driverTrips = driver?.trips || null;
  const p1Trip = getMultiloadGroupP1Trip(section);
  const hasBusyTrip = trips.some((t) => t.is_busy_trip === true || t.sequence === 0);
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id,
    disabled: hasBusyTrip, // Cannot move if any trip in group is In Progress
  });

  return (
    <div
      ref={setNodeRef}
      {...(hasBusyTrip ? {} : { ...listeners, ...attributes })}
      className={`border border-main-primary rounded-xl p-3 mb-2 bg-primary-light/20 space-y-2 ${hasBusyTrip ? 'cursor-not-allowed' : 'cursor-grab active:cursor-grabbing touch-none'} ${isDragging ? 'opacity-40' : ''}`}
    >
      {(routeStr || p1Trip) && (
        <div className="border border-primary-border rounded-lg p-1.5 bg-primary-light/40 space-y-1.5">
          {p1Trip && (
            <TripFreeTimeBanner
              trip={p1Trip}
              driverTrips={driverTrips}
              timezone={timezone || 'America/Los_Angeles'}
              asMultiloadGroup
              multiloadRoutes={multiloadRoutes}
            />
          )}
          {routeStr && (
            <div className="text-[11px] flex items-center flex-wrap gap-x-1 gap-y-0.5">
              <span className="text-primary font-semibold flex items-center gap-1">
                <Truck className="w-3 h-3 shrink-0" />
                Route:
              </span>
              <span className="font-mono font-semibold text-main-primary">{routeStr}</span>
            </div>
          )}
        </div>
      )}
      {trips.map((trip, idxInSection) => (
        <div key={trip.trip_id || trip.id || `t-${idxInSection}`}>
          <TripCardContent
            trip={trip}
            timezone={timezone}
            isUnassigned={false}
            spaceTypeMap={spaceTypeMap}
            compact
            driverTrips={driverTrips}
            multiloadRoutes={multiloadRoutes}
          />
        </div>
      ))}
    </div>
  );
}

// --- Droppable slot — thin strip at rest, card-shaped placeholder while dragging ---
// emptyColumn=true: acts as the entire empty-column target (shows "Drop trips here" when idle)
function DroppableSlot({ id, minHeight = 4, emptyColumn = false }) {
  const { isOver, setNodeRef } = useDroppable({ id });
  const { active } = useDndContext();
  const isDragging = active !== null;

  // Not dragging → invisible spacer (or styled empty-column placeholder)
  if (!isDragging) {
    if (emptyColumn) {
      return (
        <div
          ref={setNodeRef}
          className="flex min-h-[72px] items-center justify-center rounded-lg border border-dashed border-card-border bg-main-base-50 p-2 text-center text-[10px] text-textMuted"
        >
          Drop trips here
        </div>
      );
    }
    return <div ref={setNodeRef} style={{ minHeight: `${minHeight}px` }} className="flex-shrink-0" />;
  }

  // Dragging: show a card-shaped drop zone
  return (
    <div
      ref={setNodeRef}
      className={`flex-shrink-0 rounded-md border-2 border-dashed transition-all duration-150 flex items-center justify-center gap-1.5 ${
        isOver
          ? 'border-main-primary bg-main-primary/10 min-h-[60px] scale-[1.01]'
          : emptyColumn
          ? 'border-main-primary/40 bg-main-base-50/60 min-h-[72px]'
          : 'border-main-base bg-main-base-50/60 min-h-[28px]'
      }`}
    >
      {isOver && (
        <>
          <svg className="w-3.5 h-3.5 text-main-primary shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 13.5 12 21m0 0-7.5-7.5M12 21V3" />
          </svg>
          <span className="text-[11px] font-semibold text-main-primary">Drop here</span>
        </>
      )}
      {!isOver && emptyColumn && (
        <span className="text-[10px] text-main-light">Drop trips here</span>
      )}
    </div>
  );
}

// --- Unassigned sidebar (filter uses top bar search via `tripSearch`) ---
function UnassignedSidebar({ trips, timezone, spaceTypeMap = {}, tripSearch = '' }) {
  const list = trips || [];

  const filteredWithFullIndex = useMemo(() => {
    if (!searchTokensFromInput(tripSearch).length) {
      return list.map((trip, i) => ({ trip, fullIndex: i }));
    }
    return list
      .map((trip, i) => ({ trip, fullIndex: i }))
      .filter(({ trip }) => tripMatchesTripsPageSearch(trip, tripSearch, spaceTypeMap, timezone));
  }, [list, tripSearch, spaceTypeMap, timezone]);

  return (
    <div className="flex h-full flex-col bg-card-bg border-r border-card-border">
      <div className="flex-shrink-0 border-b border-card-border bg-main-base-50 px-3 py-1.5">
        <h2 className="font-semibold text-xs uppercase tracking-tight leading-none text-main-dark truncate">
          Unassigned
          <span className="ml-1 font-normal normal-case text-main-light">({list.length})</span>
        </h2>
        <p className="text-[9px] text-main-light mt-1 leading-none">Drag trips here or into driver columns</p>
      </div>
      <div className="flex-1 min-h-0 space-y-1 overflow-y-auto overflow-x-hidden p-2">
        {filteredWithFullIndex.length === 0 && list.length > 0 ? (
          <div className="text-center py-4 text-textMuted text-xs">No trips match your search.</div>
        ) : filteredWithFullIndex.length === 0 ? (
          <>
            <DroppableSlot id={`unassigned${DELIM}slot${DELIM}0`} minHeight={120} />
            <div className="text-center py-4 text-textMuted text-xs">No unassigned trips. Drop here to move back.</div>
          </>
        ) : (
          <>
            {filteredWithFullIndex.map(({ trip, fullIndex }) => (
              <React.Fragment key={trip.id || trip.trip_id || `u-${fullIndex}`}>
                <DroppableSlot id={`unassigned${DELIM}slot${DELIM}${fullIndex}`} />
                <DraggableTripCard
                  id={`unassigned${DELIM}${trip.id || trip.trip_id || `idx-${fullIndex}`}`}
                  trip={trip}
                  timezone={timezone}
                  isUnassigned
                  spaceTypeMap={spaceTypeMap}
                />
              </React.Fragment>
            ))}
            <DroppableSlot id={`unassigned${DELIM}slot${DELIM}${list.length}`} />
          </>
        )}
      </div>
    </div>
  );
}

// --- Single driver column ---
function DriverColumn({
  driver,
  timezone,
  spaceTypeMap,
  multiloadRoutes = {},
  onExportLoad,
  exportDisabled,
  exportWorking,
  bucketTripSearch = '',
}) {
  const { parts, endSlotIndex, sortedTrips } = useMemo(() => {
    const filterAssigned = searchTokensFromInput(bucketTripSearch).length > 0;
    const sorted = sortTripsForDisplay(driver.trips || []);
    const sectionList = buildTripSections(sorted);
    const p = [];
    let slotIndex = 0;
    const multiloadRouteString = (section) => {
      if (!section.multiloadGroupId || !multiloadRoutes[section.multiloadGroupId]) return null;
      const routeSteps = multiloadRoutes[section.multiloadGroupId];
      if (!Array.isArray(routeSteps) || routeSteps.length === 0) return null;
      const tripIdToLabel = {};
      let n = 1;
      routeSteps.forEach(({ trip_id: tid }) => {
        if (tid && tripIdToLabel[tid] == null) tripIdToLabel[tid] = n++;
      });
      return routeSteps
        .map(({ trip_id: tid, stop_type: st }) => {
          const label = tripIdToLabel[tid] || '?';
          return st === 'pickup' ? `P${label}` : `D${label}`;
        })
        .join(' → ');
    };

    const fleetMode = isFleetDriver(driver);
    let lastHeaderKey = Symbol('unset');
    const headerFor = (trips) => {
      if (!fleetMode) return;
      const first = trips?.[0];
      const idx = first?.fleet_window_index !== undefined && first?.fleet_window_index !== ''
        ? first.fleet_window_index
        : windowForTime(driver, first?.schedule_time);
      const key = idx == null ? 'none' : idx;
      if (key === lastHeaderKey) return;
      lastHeaderKey = key;
      const window = idx == null ? null : (driver.fleet_windows || []).find((w) => w.window_index === idx) || null;
      const windowTrips = (driver.trips || []).filter((t) => {
        const tIdx = t.fleet_window_index !== undefined && t.fleet_window_index !== ''
          ? t.fleet_window_index
          : windowForTime(driver, t.schedule_time);
        return idx == null ? tIdx == null : tIdx === idx;
      });
      p.push(
        <FleetWindowHeader
          key={`fleet-hdr-${key}`}
          window={window}
          driver={driver}
          trips={windowTrips}
          timezone={timezone}
          compact
        />
      );
    };

    sectionList.forEach((section) => {
      const isMultiload = section.multiloadGroupId != null;
      const routeStr = isMultiload ? multiloadRouteString(section) : null;
      const groupLen = section.trips?.length || 0;
      headerFor(section.trips);

      if (isMultiload) {
        const groupMatch =
          !filterAssigned ||
            section.trips?.some((t) => tripMatchesTripsPageSearch(t, bucketTripSearch, spaceTypeMap, timezone));
        if (groupMatch) {
          p.push(
            <React.Fragment key={`ml-${section.multiloadGroupId}`}>
              <DroppableSlot id={`driver${DELIM}${driver.driver_id}${DELIM}slot${DELIM}${slotIndex}`} />
              <DraggableMultiloadGroup
                id={`driver${DELIM}${driver.driver_id}${DELIM}multiload${DELIM}${String(section.multiloadGroupId)}`}
                section={section}
                routeStr={routeStr}
                driver={driver}
                timezone={timezone}
                spaceTypeMap={spaceTypeMap}
                multiloadRoutes={multiloadRoutes}
              />
            </React.Fragment>
          );
        }
        slotIndex += groupLen;
        return;
      }

      const trip = section.trips[0];
      if (!trip) {
        return;
      }
      const showTrip =
        !filterAssigned || tripMatchesTripsPageSearch(trip, bucketTripSearch, spaceTypeMap, timezone);
      if (showTrip) {
        p.push(
          <React.Fragment key={trip.trip_id || trip.id || `t-${slotIndex}`}>
            <DroppableSlot id={`driver${DELIM}${driver.driver_id}${DELIM}slot${DELIM}${slotIndex}`} />
            <DraggableTripCard
              id={`driver${DELIM}${driver.driver_id}${DELIM}${trip.id || trip.trip_id || `idx-${slotIndex}`}`}
              trip={trip}
              timezone={timezone}
              isUnassigned={false}
              spaceTypeMap={spaceTypeMap}
              driverTrips={driver.trips}
              multiloadRoutes={multiloadRoutes}
            />
          </React.Fragment>
        );
      }
      slotIndex += 1;
    });

    return { parts: p, endSlotIndex: slotIndex, sortedTrips: sorted };
  }, [driver, driver.trips, driver.driver_id, spaceTypeMap, timezone, multiloadRoutes, bucketTripSearch]);

  return (
    <div className="flex h-full min-w-[300px] max-w-[340px] flex-shrink-0 flex-col rounded-md border border-main-base bg-white cursor-default shadow-sm overflow-hidden">
      <DriverColumnHeader
        driver={driver}
        timezone={timezone}
        onExportLoad={onExportLoad}
        exportDisabled={exportDisabled}
        exportWorking={exportWorking}
      />
      <div className="flex-1 min-h-0 space-y-0.5 overflow-y-auto overflow-x-hidden p-1.5">
        {sortedTrips.length === 0 ? (
          <>
            {isFleetDriver(driver)
              ? (driver.fleet_windows || []).map((window) => (
                  <FleetWindowHeader
                    key={`fleet-hdr-${window.window_index}`}
                    window={window}
                    driver={driver}
                    trips={[]}
                    timezone={timezone}
                    compact
                  />
                ))
              : null}
            <DroppableSlot
              id={`driver${DELIM}${driver.driver_id}${DELIM}slot${DELIM}${endSlotIndex}`}
              emptyColumn
            />
          </>
        ) : (
          <>
            {searchTokensFromInput(bucketTripSearch).length > 0 && parts.length === 0 ? (
              <p className="px-1 py-3 text-center text-textMuted text-xs">No trips match your search.</p>
            ) : null}
            {parts}
            <DroppableSlot id={`driver${DELIM}${driver.driver_id}${DELIM}slot${DELIM}${endSlotIndex}`} />
          </>
        )}
      </div>
    </div>
  );
}

const DELIM = '::';

// --- Parse drag/drop ids (use :: so driver_id / trip id can contain dashes) ---
function parseActiveId(activeId, drivers, unassignedTrips) {
  const s = String(activeId);
  const parts = s.split(DELIM);
  if (parts[0] === 'unassigned' && parts.length >= 2) {
    const tripId = parts.slice(1).join(DELIM);
    const trip = (unassignedTrips || []).find((t) => String(t.id || t.trip_id) === tripId);
    return { type: 'unassigned', trip, tripId };
  }
  if (parts[0] === 'driver' && parts.length >= 4 && parts[2] === 'multiload') {
    const driverId = parts[1];
    const groupId = parts.slice(3).join(DELIM);
    const driver = (drivers || []).find((d) => String(d.driver_id) === driverId);
    const sorted = driver?.trips ? sortTripsForDisplay(driver.trips) : [];
    const trips = sorted.filter((t) => t.multiload_group_id != null && String(t.multiload_group_id) === groupId);
    return { type: 'multiload', driverId, driver, groupId, trips };
  }
  if (parts[0] === 'driver' && parts.length >= 3) {
    const driverId = parts[1];
    const tripId = parts.slice(2).join(DELIM);
    const driver = (drivers || []).find((d) => String(d.driver_id) === driverId);
    const trip = driver?.trips?.find((t) => String(t.id || t.trip_id) === tripId);
    return { type: 'driver', driverId, driver, trip, tripId };
  }
  return null;
}

function parseOverId(overId) {
  const s = String(overId);
  const parts = s.split(DELIM);
  if (parts[0] === 'unassigned' && parts[1] === 'slot' && parts.length >= 3) {
    return { container: 'unassigned', index: parseInt(parts[2], 10) };
  }
  if (parts[0] === 'driver' && parts[1] !== 'slot' && parts[2] === 'slot' && parts.length >= 4) {
    const driverId = parts[1];
    const index = parseInt(parts[3], 10);
    return { container: 'driver', driverId, index };
  }
  return null;
}

export default function BucketPage() {
  const {
    selectedResultId,
    setOnPublishSuccess,
    firePublishSuccess,
    patchAssignmentSessionCache,
  } = useTimeFilter();
  const { adminModeUnlocked, publishSelectionUnlocked } = usePublishAccess();
  const bucketRouteEnabled = useAssignmentRouteEnabled('bucket');
  const {
    normalized: tripsNormalized,
    isLoading: sessionQueryLoading,
    error: sessionQueryError,
  } = useTripsAssignment(selectedResultId, { enabled: bucketRouteEnabled });
  const multiloadQuery = useMultiloadRoutes(selectedResultId, {
    enabled: bucketRouteEnabled,
  });
  const assignmentData = useMemo(() => {
    if (!tripsNormalized) return null;
    return {
      ...tripsNormalized,
      multiload_routes:
        multiloadQuery.chunkMap?.multiload_routes ||
        tripsNormalized.multiload_routes ||
        {},
    };
  }, [tripsNormalized, multiloadQuery.chunkMap]);
  const [saveError, setSaveError] = useState(null);
  const [activeId, setActiveId] = useState(null);
  /** Step 1: confirm; step 2: publish after the same validate flow as full Publish. */
  const [driverExport, setDriverExport] = useState(null);
  const [exportInProgressId, setExportInProgressId] = useState(null);
  const [exportError, setExportError] = useState(null);
  /** Driver ids waiting for GIS estimate recalc (serial queue). */
  const [dirtyDriverIds, setDirtyDriverIds] = useState(() => new Set());
  const [recalcLoading, setRecalcLoading] = useState(false);
  const [recalcError, setRecalcError] = useState(null);
  const pendingEstimateIdsRef = useRef(new Set());
  const estimateQueueRunningRef = useRef(false);
  const estimateRetryTimerRef = useRef(null);
  const assignmentDataRef = useRef(assignmentData);
  assignmentDataRef.current = assignmentData;

  const loading = sessionQueryLoading && !assignmentData;
  const error = sessionQueryError
    ? sessionQueryError.message || 'Failed to load assignment'
    : (!loading && selectedResultId && !assignmentData ? 'Result not found' : null);

  const ESTIMATE_RETRY_MS = 3000;

  const syncDirtyDriverUi = useCallback(() => {
    setDirtyDriverIds(new Set(pendingEstimateIdsRef.current));
  }, []);

  // Clear estimate queue when switching sessions
  useEffect(() => {
    pendingEstimateIdsRef.current = new Set();
    estimateQueueRunningRef.current = false;
    if (estimateRetryTimerRef.current) {
      clearTimeout(estimateRetryTimerRef.current);
      estimateRetryTimerRef.current = null;
    }
    setDirtyDriverIds(new Set());
    setRecalcError(null);
    setRecalcLoading(false);
  }, [selectedResultId]);

  useEffect(() => {
    return () => {
      if (estimateRetryTimerRef.current) {
        clearTimeout(estimateRetryTimerRef.current);
        estimateRetryTimerRef.current = null;
      }
    };
  }, []);

  const pumpEstimateQueue = useCallback(async () => {
    if (estimateQueueRunningRef.current) return;
    if (pendingEstimateIdsRef.current.size === 0) {
      setRecalcLoading(false);
      syncDirtyDriverUi();
      return;
    }

    const data = assignmentDataRef.current;
    const resultId = data?.id || selectedResultId;
    if (!data || !resultId) return;

    estimateQueueRunningRef.current = true;
    setRecalcLoading(true);
    setRecalcError(null);

    // Snapshot pending ids for this job; new moves during the call re-enter the set
    const dirty = [...pendingEstimateIdsRef.current];
    pendingEstimateIdsRef.current.clear();
    syncDirtyDriverUi();

    const fleetSession = isFleetSession(data);
    const sourceByPseudo = new Map();
    const driversPayload = [];
    for (const d of (data.drivers || []).filter((row) => dirty.includes(String(row.driver_id)))) {
      const copy = { ...d, trips: (d.trips || []).map((t) => ({ ...t })) };
      if (fleetSession && isFleetDriver(copy)) {
        const parts = splitDriverForEstimate(copy);
        if (parts.length) {
          for (const part of parts) {
            sourceByPseudo.set(String(part.driver_id), String(d.driver_id));
            driversPayload.push(part);
          }
          continue;
        }
      }
      driversPayload.push(copy);
    }

    if (!driversPayload.length) {
      estimateQueueRunningRef.current = false;
      // Continue if more arrived while we were filtering
      if (pendingEstimateIdsRef.current.size > 0) {
        queueMicrotask(() => { pumpEstimateQueue(); });
      } else {
        setRecalcLoading(false);
      }
      return;
    }

    const routes = data.multiload_routes || {};
    const relevantRoutes = {};
    for (const d of driversPayload) {
      for (const t of d.trips || []) {
        const gid = t.multiload_group_id;
        if (gid && t.is_multiload && routes[gid]) relevantRoutes[gid] = routes[gid];
      }
    }

    try {
      const res = await schedulerApiFetch('estimate-trip-details', {
        method: 'POST',
        withAuth: true,
        body: {
          result_id: resultId,
          drivers: driversPayload,
          multiload_routes: relevantRoutes,
        },
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.detail || errBody.error || res.statusText || 'Estimate failed');
      }
      const body = await res.json();
      if (!body.success) throw new Error(body.error || 'Estimate failed');

      // Merge estimates onto latest trip order (moves during flight must not be overwritten)
      const latest = assignmentDataRef.current;
      const byId = new Map(
        (body.drivers || []).map((d) => [String(d.driver_id), d])
      );
      let newDrivers = (latest?.drivers || data.drivers || []).map((d) => {
        const realId = String(d.driver_id);
        if (fleetSession && isFleetDriver(d)) {
          const parts = (body.drivers || []).filter((est) => {
            const src = sourceByPseudo.get(String(est.driver_id));
            return src === realId || String(est.driver_id) === realId;
          });
          if (!parts.length) return d;
          return stitchEstimates(d, parts);
        }
        const estimated = byId.get(realId);
        if (!estimated) return d;
        const estByTrip = new Map(
          (estimated.trips || []).map((t) => [String(t.trip_id || t.id), t])
        );
        return {
          ...d,
          trips: (d.trips || []).map((t) => {
            const est = estByTrip.get(String(t.trip_id || t.id));
            if (!est) return t;
            return {
              ...t,
              trip_details: est.trip_details ?? t.trip_details,
              deadhead_miles: est.deadhead_miles ?? t.deadhead_miles,
              eta_to_pickup_minutes: est.eta_to_pickup_minutes ?? t.eta_to_pickup_minutes,
              loadTime: est.loadTime ?? t.loadTime,
              unloadTime: est.unloadTime ?? t.unloadTime,
            };
          }),
        };
      });
      newDrivers = recomputeDriverTotals(newDrivers);

      patchAssignmentSessionCache(resultId, { drivers: newDrivers });
      const logEntry = {
        timestamp: new Date().toISOString(),
        trip_id: 'recalculate_estimates',
        trip_client: '(estimates)',
        action: 'recalculate_estimates',
        reason: `Recalculated GIS estimates for ${dirty.length} driver bucket(s)`,
        driver_name: dirty.join(', '),
        assignment_reason: `Recalculated estimates for drivers: ${dirty.join(', ')}`,
        assigned: true,
        dirty_driver_ids: dirty,
      };
      await updateAssignmentChunksAndAppendLog(
        resultId,
        { drivers: newDrivers },
        logEntry
      );
      patchAssignmentSessionCache(resultId, { invalidateLogs: true });
      setRecalcError(null);

      estimateQueueRunningRef.current = false;
      if (pendingEstimateIdsRef.current.size > 0) {
        queueMicrotask(() => { pumpEstimateQueue(); });
      } else {
        setRecalcLoading(false);
        syncDirtyDriverUi();
      }
    } catch (e) {
      console.error(e);
      setRecalcError(e.message || 'Estimate update failed — retrying…');
      // Do not roll back trip moves — re-queue and retry later
      for (const id of dirty) pendingEstimateIdsRef.current.add(id);
      syncDirtyDriverUi();
      estimateQueueRunningRef.current = false;
      if (estimateRetryTimerRef.current) clearTimeout(estimateRetryTimerRef.current);
      estimateRetryTimerRef.current = setTimeout(() => {
        estimateRetryTimerRef.current = null;
        pumpEstimateQueue();
      }, ESTIMATE_RETRY_MS);
    }
  }, [selectedResultId, patchAssignmentSessionCache, syncDirtyDriverUi]);

  /** Enqueue affected drivers for serial GIS estimate recalc (auto after manual moves). */
  const enqueueEstimateRecalc = useCallback((ids, driversOverride) => {
    let added = false;
    for (const id of ids || []) {
      if (!id) continue;
      const key = String(id);
      if (!pendingEstimateIdsRef.current.has(key)) added = true;
      pendingEstimateIdsRef.current.add(key);
    }
    if (driversOverride && assignmentDataRef.current) {
      assignmentDataRef.current = {
        ...assignmentDataRef.current,
        drivers: driversOverride,
      };
    }
    syncDirtyDriverUi();
    if (added || pendingEstimateIdsRef.current.size > 0) {
      // Let React Query cache settle after patch, then pump
      queueMicrotask(() => { pumpEstimateQueue(); });
    }
  }, [pumpEstimateQueue, syncDirtyDriverUi]);

  const markDriversDirty = enqueueEstimateRecalc;

  const handlePublishSuccessForBucket = useCallback(() => {
    if (!selectedResultId) return;
    patchAssignmentSessionCache(selectedResultId, { publishFlags: { isPublished: true } });
  }, [selectedResultId, patchAssignmentSessionCache]);

  useEffect(() => {
    setOnPublishSuccess(() => handlePublishSuccessForBucket);
    return () => setOnPublishSuccess(null);
  }, [setOnPublishSuccess, handlePublishSuccessForBucket]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 120, tolerance: 5 } })
  );

  /** Strip assignment-specific fields so a trip looks like an original unassigned trip when moved to the sidebar. */
  const toUnassignedTrip = (trip, reason) => ({
    ...trip,
    reason,
    // Clear travel-bar data — these belong to driver assignment, not unassigned state
    deadhead_miles: 0,
    eta_to_pickup_minutes: null,
    available_in_minutes: null,
    // Clear sequencing & live-status fields
    sequence: null,
    is_busy_trip: false,
    trip_status: null,
  });

  const handleDragStart = (event) => setActiveId(event.active.id);
  const handleDragEnd = (event) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || !assignmentData) return;

    const drivers = assignmentData.drivers || [];
    const unassignedTrips = assignmentData.unassigned_trips || [];
    const source = parseActiveId(active.id, drivers, unassignedTrips);
    const target = parseOverId(over.id);
    if (!target) return;

    // Reject move if source is an In Progress trip (cannot reassign during planning)
    const isBusyTrip = (t) => t?.is_busy_trip === true || t?.sequence === 0;
    if (source?.type === 'multiload' && source.trips?.some(isBusyTrip)) return;
    if (source?.type === 'driver' && source.trip && isBusyTrip(source.trip)) return;

    let newDrivers = drivers.map((d) => ({ ...d, trips: [...(d.trips || [])] }));
    let newUnassigned = [...unassignedTrips];

    // --- Multiload group move (whole group as one) ---
    if (source?.type === 'multiload' && source.trips?.length) {
      const fromLabel = source.driver?.driver_name || source.driverId;
      const toLabel = target.container === 'unassigned'
        ? 'Unassigned'
        : (newDrivers.find((d) => String(d.driver_id) === target.driverId)?.driver_name || target.driverId);

      const tripsToMove = source.trips.map((t) => {
        let trip = { ...t, manually_adjusted: true, new_assignment: false };
        if (target.container === 'unassigned') {
          // Dissolve group: strip multiload so each trip is a single trip in unassigned; when moved back to driver they go one-by-one with no multiload border/route
          trip = stripMultiloadFields(trip);
        } else {
          normalizeTripCoords(trip);
          const prefInfo = trip.preferred_driver_info;
          const preferredDriverId = prefInfo?.preferred_driver_id != null ? String(prefInfo.preferred_driver_id) : null;
          const isPreferredDriver = preferredDriverId && String(target.driverId) === preferredDriverId;
          if (isPreferredDriver) {
            trip.preferred_driver_violation = false;
            trip.driver_violation = false;
          } else if (preferredDriverId && String(target.driverId) !== preferredDriverId) {
            trip.preferred_driver_violation = true;
            trip.driver_violation = false;
          }
        }
        return trip;
      });

      const tripKeys = new Set(tripsToMove.map((t) => String(t.id || t.trip_id || '')));
      const matchAnyInGroup = (t) => tripKeys.has(String(t.id || t.trip_id || ''));

      // For same-driver multiload reorder: capture the group's visual position before removal
      // so we can adjust the target index after the trips are removed (they shift positions down).
      let mlSrcMinVisualIdx = -1;
      let mlSrcGroupSize = 0;
      if (source.driverId === target.driverId && target.container === 'driver') {
        const _di = newDrivers.findIndex((d) => String(d.driver_id) === source.driverId);
        if (_di >= 0) {
          const _sorted = sortTripsForDisplay(newDrivers[_di].trips);
          const _idxs = _sorted.map((t, i) => (matchAnyInGroup(t) ? i : -1)).filter((i) => i >= 0);
          if (_idxs.length > 0) { mlSrcMinVisualIdx = Math.min(..._idxs); mlSrcGroupSize = _idxs.length; }
        }
      }

      const diSource = newDrivers.findIndex((d) => String(d.driver_id) === source.driverId);
      if (diSource >= 0) {
        newDrivers[diSource].trips = newDrivers[diSource].trips.filter((t) => !matchAnyInGroup(t));
        newDrivers[diSource].trips = reindexSequences(newDrivers[diSource].trips);
      }

      if (target.container === 'unassigned') {
        const reason = `Manually moved multiload group from ${fromLabel} to unassigned`;
        const toInsert = tripsToMove.map((t) => toUnassignedTrip(t, reason));
        newUnassigned.splice(target.index, 0, ...toInsert);
      } else {
        const diTarget = newDrivers.findIndex((d) => String(d.driver_id) === target.driverId);
        if (diTarget >= 0) {
          // Insert into the visually-sorted array so position matches what the user sees
          const sorted = sortTripsForDisplay(newDrivers[diTarget].trips);
          let insertIdx = target.index;
          // Adjust for same-driver reorder: removing the group shifts later slots down
          if (mlSrcMinVisualIdx >= 0 && mlSrcMinVisualIdx < target.index) insertIdx -= mlSrcGroupSize;
          insertIdx = Math.max(0, Math.min(insertIdx, sorted.length));
          sorted.splice(insertIdx, 0, ...tripsToMove);
          newDrivers[diTarget].trips = reindexSequences(sorted);
        }
      }

      const affectedDriverIds = new Set([String(source.driverId)]);
      if (target.container !== 'unassigned' && target.driverId) affectedDriverIds.add(String(target.driverId));
      let gapWarning = false;
      for (const did of affectedDriverIds) {
        const driver = newDrivers.find((d) => String(d.driver_id) === did);
        if (!driver?.trips?.length) continue;
        for (const t of driver.trips) {
          if (!t.is_break && (t.pickup_lat == null || t.dropoff_lat == null)) normalizeTripCoords(t);
        }
        if (isFleetSession(assignmentData) && isFleetDriver(driver) && target.container === 'driver' && String(target.driverId) === did) {
          if (retagDriverWindows(driver)) gapWarning = true;
        }
        recomputeDeadheadMaybeFleet(driver, assignmentData?.multiload_routes || {}, assignmentData);
      }

      newDrivers = recomputeDriverTotals(newDrivers);

      const nextData = { ...assignmentData, drivers: newDrivers, unassigned_trips: newUnassigned };
      setSaveError(gapWarning ? 'Trip is outside every fleet window for this driver' : null);

      const resultId = assignmentData.id || selectedResultId;
      if (!resultId) return;

      const n = tripsToMove.length;
      const reasonText = `Manually adjusted multiload group (${n} trip${n !== 1 ? 's' : ''}) from ${fromLabel} to ${toLabel}.`;
      const firstTrip = tripsToMove[0];
      const logEntry = {
        timestamp: new Date().toISOString(),
        trip_id: firstTrip?.trip_id || firstTrip?.id || `multiload_group_${source.groupId}`,
        trip_client: firstTrip?.client_name || '(multiload group)',
        action: 'manual_move',
        from: fromLabel,
        to: toLabel,
        reason: reasonText,
        driver_name: `Manual: ${fromLabel} → ${toLabel}`,
        assignment_reason: reasonText,
        assigned: target.container !== 'unassigned',
        multiload_group: true,
        trip_count: n,
      };

      const prevDrivers = assignmentData.drivers;
      const prevUnassigned = assignmentData.unassigned_trips;
      patchAssignmentSessionCache(resultId, {
        drivers: newDrivers,
        unassigned_trips: newUnassigned,
      });
      markDriversDirty(affectedDriverIds, newDrivers);
      (async () => {
        try {
          await updateAssignmentChunksAndAppendLog(resultId, { drivers: newDrivers, unassigned_trips: newUnassigned }, logEntry);
          patchAssignmentSessionCache(resultId, { invalidateLogs: true });
        } catch (err) {
          console.error(err);
          setSaveError(err.message || 'Failed to save');
          patchAssignmentSessionCache(resultId, {
            drivers: prevDrivers,
            unassigned_trips: prevUnassigned,
          });
        }
      })();
      return;
    }

    // --- Single trip move ---
    if (!source?.trip) return;

    let trip = { ...source.trip, manually_adjusted: true, new_assignment: false };
    if (source.type === 'unassigned' && target.container === 'driver') {
      trip = stampManualAddRevenue(trip, { fromUnassigned: true });
    }
    if (target.container !== 'unassigned') {
      normalizeTripCoords(trip);
      const prefInfo = trip.preferred_driver_info;
      const preferredDriverId = prefInfo?.preferred_driver_id != null ? String(prefInfo.preferred_driver_id) : null;
      const isPreferredDriver = preferredDriverId && String(target.driverId) === preferredDriverId;
      if (isPreferredDriver) {
        trip.preferred_driver_violation = false;
        trip.driver_violation = false;
      } else if (preferredDriverId && String(target.driverId) !== preferredDriverId) {
        trip.preferred_driver_violation = true;
        trip.driver_violation = false;
      }
    }

    const tripKey = String(trip.id || trip.trip_id || '');
    const matchTrip = (t) => String(t.id || t.trip_id || '') === tripKey;

    // Capture source visual index before removal for same-list index adjustment.
    // When the source is removed from a position before the target slot, every slot
    // after it shifts down by 1, so we must decrement target.index by 1 in that case.
    let srcDriverVisualIdx = -1;
    if (source.type === 'driver' && target.container === 'driver' && source.driverId === target.driverId) {
      const _di = newDrivers.findIndex((d) => String(d.driver_id) === source.driverId);
      if (_di >= 0) srcDriverVisualIdx = sortTripsForDisplay(newDrivers[_di].trips).findIndex((t) => matchTrip(t));
    }
    let srcUnassignedIdx = -1;
    if (source.type === 'unassigned' && target.container === 'unassigned') {
      srcUnassignedIdx = newUnassigned.findIndex((t) => matchTrip(t));
    }

    // Remove from source
    if (source.type === 'unassigned') {
      newUnassigned = newUnassigned.filter((t) => !matchTrip(t));
    } else {
      const di = newDrivers.findIndex((d) => String(d.driver_id) === source.driverId);
      if (di >= 0) {
        newDrivers[di].trips = newDrivers[di].trips.filter((t) => !matchTrip(t));
      }
    }

    // Insert into target
    if (target.container === 'unassigned') {
      let insertIdx = target.index;
      if (srcUnassignedIdx >= 0 && srcUnassignedIdx < target.index) insertIdx -= 1;
      insertIdx = Math.max(0, Math.min(insertIdx, newUnassigned.length));
      const reason = source.type === 'driver'
        ? `Manually moved from ${source.driver?.driver_name || source.driverId} to unassigned`
        : (trip.reason || 'Unassigned');
      newUnassigned.splice(insertIdx, 0, toUnassignedTrip(trip, reason));
    } else {
      const di = newDrivers.findIndex((d) => String(d.driver_id) === target.driverId);
      if (di >= 0) {
        // Insert into the visually-sorted array so position matches what the user sees
        const sorted = sortTripsForDisplay(newDrivers[di].trips);
        let insertIdx = target.index;
        // Adjust for same-driver reorder: source was removed from a slot before the target
        if (srcDriverVisualIdx >= 0 && srcDriverVisualIdx < target.index) insertIdx -= 1;
        insertIdx = Math.max(0, Math.min(insertIdx, sorted.length));
        sorted.splice(insertIdx, 0, trip);
        newDrivers[di].trips = reindexSequences(sorted);
      }
    }

    // Reindex source driver — skip if same as target driver (already reindexed above)
    if (source.type === 'driver' && source.driverId && !(target.container === 'driver' && source.driverId === target.driverId)) {
      const di = newDrivers.findIndex((d) => String(d.driver_id) === source.driverId);
      if (di >= 0) newDrivers[di].trips = reindexSequences(newDrivers[di].trips);
    }

    const affectedDriverIds = new Set();
    if (source.type === 'driver' && source.driverId) affectedDriverIds.add(String(source.driverId));
    if (target.container !== 'unassigned' && target.driverId) affectedDriverIds.add(String(target.driverId));
    let gapWarning = false;
    for (const did of affectedDriverIds) {
      const driver = newDrivers.find((d) => String(d.driver_id) === did);
      if (!driver?.trips?.length) continue;
      for (const t of driver.trips) {
        if (!t.is_break && (t.pickup_lat == null || t.dropoff_lat == null)) normalizeTripCoords(t);
      }
      if (isFleetSession(assignmentData) && isFleetDriver(driver) && target.container === 'driver' && String(target.driverId) === did) {
        if (retagDriverWindows(driver)) gapWarning = true;
      }
      recomputeDeadheadMaybeFleet(driver, assignmentData?.multiload_routes || {}, assignmentData);
    }

    newDrivers = recomputeDriverTotals(newDrivers);

    const nextData = { ...assignmentData, drivers: newDrivers, unassigned_trips: newUnassigned };
    setSaveError(gapWarning ? 'Trip is outside every fleet window for this driver' : null);

    const resultId = assignmentData.id || selectedResultId;
    if (!resultId) return;

    const fromLabel = source.type === 'unassigned' ? 'Unassigned' : (source.driver?.driver_name || source.driverId);
    const toLabel = target.container === 'unassigned' ? 'Unassigned' : (newDrivers.find((d) => d.driver_id === target.driverId)?.driver_name || target.driverId);
    const reasonText = `Manually adjusted from ${fromLabel} to ${toLabel}.`;

    const logEntry = {
      timestamp: new Date().toISOString(),
      trip_id: trip.trip_id || trip.id,
      trip_client: trip.client_name,
      action: 'manual_move',
      from: fromLabel,
      to: toLabel,
      reason: reasonText,
      driver_name: `Manual: ${fromLabel} → ${toLabel}`,
      assignment_reason: reasonText,
      assigned: true,
    };

    const prevDrivers = assignmentData.drivers;
    const prevUnassigned = assignmentData.unassigned_trips;
    patchAssignmentSessionCache(resultId, {
      drivers: newDrivers,
      unassigned_trips: newUnassigned,
    });
    markDriversDirty(affectedDriverIds, newDrivers);
    (async () => {
      try {
        await updateAssignmentChunksAndAppendLog(resultId, { drivers: newDrivers, unassigned_trips: newUnassigned }, logEntry);
        patchAssignmentSessionCache(resultId, { invalidateLogs: true });
      } catch (err) {
        console.error(err);
        setSaveError(err.message || 'Failed to save');
        patchAssignmentSessionCache(resultId, {
          drivers: prevDrivers,
          unassigned_trips: prevUnassigned,
        });
      }
    })();
  };

  const companyTimezone = assignmentData?.metadata?.timezone || 'America/Los_Angeles';
  const drivers = useMemo(() => {
    const raw = assignmentData?.drivers || [];
    return [...raw].sort((a, b) => {
      const toMs = (iso) => {
        if (!iso) return 0;
        const t = new Date(iso).getTime();
        return isNaN(t) ? 0 : t;
      };
      const aStart = toMs(a.shift_window?.shift_start);
      const bStart = toMs(b.shift_window?.shift_start);
      if (aStart !== bStart) return aStart - bStart;
      const aEnd = toMs(a.shift_window?.shift_end);
      const bEnd = toMs(b.shift_window?.shift_end);
      return aEnd - bEnd;
    });
  }, [assignmentData?.drivers]);
  const unassignedTrips = assignmentData?.unassigned_trips || [];
  const tripSearchSpaceTypeMap = assignmentData?.metadata?.load_unload_times || {};
  const hasData = assignmentData && (drivers.length > 0 || unassignedTrips.length > 0);

  const [selectedDriverIds, setSelectedDriverIds] = useState(() => new Set());
  const [driverSelectOpen, setDriverSelectOpen] = useState(false);
  const [driverSearch, setDriverSearch] = useState('');
  const [bucketTripSearch, setBucketTripSearch] = useState('');
  const [debouncedBucketTripSearch, setDebouncedBucketTripSearch] = useState('');
  const [tempTripFeasibilityOpen, setTempTripFeasibilityOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedBucketTripSearch(bucketTripSearch), 300);
    return () => clearTimeout(timer);
  }, [bucketTripSearch]);

  const driversAfterSelection = useMemo(() => {
    if (!drivers.length) return [];
    if (selectedDriverIds.size === 0) return drivers; // no selection = show all
    return drivers.filter((d) => selectedDriverIds.has(String(d.driver_id)));
  }, [drivers, selectedDriverIds]);

  const driversToShow = useMemo(() => {
    const q = debouncedBucketTripSearch;
    if (!searchTokensFromInput(q).length) return driversAfterSelection;
    return driversAfterSelection.filter((d) => {
      if (driverNameMatchesSearch(d.driver_name, q)) return true;
      for (const t of d.trips || []) {
        if (t.is_break) continue;
        if (tripMatchesTripsPageSearch(t, q, tripSearchSpaceTypeMap, companyTimezone)) return true;
      }
      return false;
    });
  }, [driversAfterSelection, debouncedBucketTripSearch, tripSearchSpaceTypeMap, companyTimezone]);

  const bucketTripMatchCount = useMemo(() => {
    const q = debouncedBucketTripSearch;
    if (!searchTokensFromInput(q).length) return null;
    const map = tripSearchSpaceTypeMap;
    let n = 0;
    for (const d of driversToShow) {
      for (const t of d.trips || []) {
        if (t.is_break) continue;
        if (tripMatchesTripsPageSearch(t, q, map, companyTimezone)) n += 1;
      }
    }
    for (const t of unassignedTrips) {
      if (t.is_break) continue;
      if (tripMatchesTripsPageSearch(t, q, map, companyTimezone)) n += 1;
    }
    return n;
  }, [driversToShow, unassignedTrips, debouncedBucketTripSearch, tripSearchSpaceTypeMap, companyTimezone]);

  const toggleDriver = (driverId) => {
    const id = String(driverId);
    setSelectedDriverIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllDrivers = () => setSelectedDriverIds(new Set(drivers.map((d) => String(d.driver_id))));
  const clearDriverSelection = () => setSelectedDriverIds(new Set());

  const activeSource = activeId ? parseActiveId(activeId, drivers, unassignedTrips) : null;
  const activeTrip = activeSource?.type === 'multiload' ? null : activeSource?.trip ?? null;
  const activeMultiloadTrips = activeSource?.type === 'multiload' ? (activeSource.trips || []) : null;
  const activeIsUnassigned = activeId ? String(activeId).startsWith(`unassigned${DELIM}`) : false;

  const getAuthToken = useCallback(async () => {
    const u = auth.currentUser;
    if (u) return u.getIdToken();
    return null;
  }, []);

  const canExportDriver = (driver) => {
    const payload = buildPlannerSingleDriverPayload(assignmentData, driver);
    if (!payload.data.length) return false;
    const trips = (driver.trips || []).filter((t) => !t.is_break);
    return !hasPastDatedPlanTrips(trips, companyTimezone);
  };

  const startDriverExport = (driver) => {
    setExportError(null);
    if (!selectedResultId) { setExportError('Please select a planning session first.'); return; }
    const payload = buildPlannerSingleDriverPayload(assignmentData, driver);
    if (payload.data.length === 0) { setExportError('No exportable trips for this driver in this session.'); return; }
    const trips = (driver.trips || []).filter((t) => !t.is_break);
    if (hasPastDatedPlanTrips(trips, companyTimezone)) { setExportError('Past date plan cannot be published.'); return; }
    setDriverExport({ step: 1, driver });
  };


  const handleCloseDriverExport = () => {
    setDriverExport(null);
    setExportError(null);
  };

  const handleDriverExportBackToStep1 = () => {
    if (!driverExport || driverExport.step !== 2) return;
    setDriverExport({ step: 1, driver: driverExport.driver });
    setExportError(null);
  };

  const handleDriverExportStep1 = async () => {
    if (!driverExport || driverExport.step !== 1) return;
    const { driver } = driverExport;
    const progressId = resolvePlannerDriverIdForPortal(driver) || String(driver.driver_id);
    setExportInProgressId(progressId);
    setExportError(null);
    try {
      const token = await getAuthToken();
      if (!token) throw new Error('Authentication required. Please sign in again.');
      const payload = buildPlannerSingleDriverPayload(assignmentData, driver);
      if (payload.data.length === 0) throw new Error('No trips to publish.');
      const allIds = [];
      payload.data.forEach((item) => {
        (item.selectedTrips || []).forEach((id) => {
          const s = String(id);
          if (s && !allIds.includes(s)) allIds.push(s);
        });
      });
      if (allIds.length === 0) throw new Error('No trip IDs to validate.');
      const validateResponse = await schedulerApiFetch('validate', { method: 'POST', bearerToken: token, body: { _ids: allIds } });
      if (!validateResponse.ok) {
        const errorData = await validateResponse.json().catch(() => ({}));
        throw new Error(`Validation failed: ${errorData.detail || validateResponse.statusText}`);
      }
      const validateResult = await validateResponse.json();
      const validatedIds = (validateResult.validTripIds || validateResult._ids || []).map((id) => String(id));
      const filtered = filterPayloadByValidatedTripIds(payload, validatedIds);
      if (filtered.length === 0) throw new Error('No trips to publish after validation.');
      const validTripCount = filtered.reduce((n, it) => n + (it.selectedTrips || []).length, 0);
      setDriverExport({
        step: 2,
        driver,
        dataRows: filtered,
        authToken: token,
        validTripCount,
      });
    } catch (e) {
      console.error(e);
      setExportError(e.message);
    } finally {
      setExportInProgressId(null);
    }
  };

  const handleDriverExportStep2 = async () => {
    if (!driverExport || driverExport.step !== 2) return;
    const { driver, dataRows, authToken } = driverExport;
    const progressId = resolvePlannerDriverIdForPortal(driver) || String(driver.driver_id);
    setExportInProgressId(progressId);
    setExportError(null);
    try {
      const data = dataRows.map((d) => ({ ...d, selectedTrips: [...(d.selectedTrips || [])] }));
      reprioritizePublishPayloadData(data);
      const lineOrderPayload = buildAssignLinesOrderPayload(assignmentData, data, {
        publishType: 'planner',
        onlyDriverId: resolvePlannerDriverIdForPortal(driver) || String(driver.driver_id),
      });
      const publishRequest = schedulerApiFetch('publish', { method: 'POST', bearerToken: authToken, body: { data } });
      const lineOrderRequest = lineOrderPayload.data.length > 0
        ? schedulerApiFetch('publish-line-order', { method: 'POST', bearerToken: authToken, body: lineOrderPayload })
        : Promise.resolve({ ok: true });
      const [publishResult, lineOrderResult] = await Promise.allSettled([publishRequest, lineOrderRequest]);

      if (publishResult.status === 'rejected') {
        throw new Error(`Publishing failed: ${publishResult.reason?.message || publishResult.reason || 'Network error'}`);
      }

      const publishResponse = publishResult.value;
      if (!publishResponse.ok) {
        const errorData = await publishResponse.json().catch(() => ({}));
        throw new Error(`Publishing failed: ${errorData.detail || publishResponse.statusText}`);
      }

      const routePublishFailed = lineOrderResult.status === 'rejected' || !lineOrderResult.value.ok;
      await updateAssignmentMetadata(selectedResultId, {
        isPublished: true,
        routePublishFailed,
        routePublishFailedAt: routePublishFailed ? new Date().toISOString() : null,
      });
      patchAssignmentSessionCache(selectedResultId, {
        publishFlags: {
          isPublished: true,
          routePublishFailed,
          routePublishFailedAt: routePublishFailed ? new Date().toISOString() : null,
        },
      });
      setDriverExport(null);
      if (firePublishSuccess) firePublishSuccess('planner');
    } catch (e) {
      console.error(e);
      setExportError(e.message);
    } finally {
      setExportInProgressId(null);
    }
  };

  return (
    <div className="h-[calc(100vh-72px)] flex flex-col">
      <div className="flex flex-col flex-1 min-h-0">
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            <span className="ml-3 text-sm text-textPrimary">Loading…</span>
          </div>
        )}

        {error && (
          <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {error}
          </div>
        )}

        {saveError && (
          <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            Save failed: {saveError}
          </div>
        )}

        {recalcError && (
          <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            {recalcError}
          </div>
        )}

        {!loading && hasData && (recalcLoading || dirtyDriverIds.size > 0) && (
          <div className="flex items-center gap-2 px-1 py-1.5 border-b border-main-base bg-main-base-50 text-[10px] font-medium text-main-light">
            <span className="h-3 w-3 animate-spin rounded-full border-2 border-main-primary border-t-transparent" />
            Updating estimates…
            {dirtyDriverIds.size > 0 ? ` (${dirtyDriverIds.size})` : ''}
          </div>
        )}

        {!loading && !hasData && (
          <div className="rounded-lg border border-card-border bg-card-bg p-8 text-center">
            <p className="text-textPrimary">Select a timestamp to view and edit buckets.</p>
          </div>
        )}

        {!loading && hasData && (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <div className="flex flex-col flex-1 min-h-0">
              <TempTripFeasibilityPanel
                isOpen={tempTripFeasibilityOpen}
                onClose={() => setTempTripFeasibilityOpen(false)}
                assignmentData={assignmentData}
              />
              <div className="flex flex-col flex-1 min-h-0 gap-1">
              {/* Select drivers bar: search + multi-select, only selected columns show */}
              <div className="flex shrink-0 items-center gap-2 flex-wrap py-0.5">
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setDriverSelectOpen((o) => !o)}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-gray-200 text-xs font-medium text-textPrimary bg-white hover:bg-gray-50"
                  >
                    <span>
                      {selectedDriverIds.size === 0
                        ? `All drivers (${drivers.length})`
                        : `Drivers: ${selectedDriverIds.size} selected`}
                    </span>
                    <svg className={`h-3.5 w-3.5 shrink-0 text-main-light transition-transform ${driverSelectOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {driverSelectOpen && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setDriverSelectOpen(false)} aria-hidden />
                      <div
                        className="absolute left-0 top-full mt-1 z-20 w-72 rounded-lg border border-card-border bg-card-bg shadow-xl py-2 max-h-80 flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="px-2 pb-2 border-b border-card-border">
                          <input
                            type="text"
                            placeholder="Search driver name..."
                            value={driverSearch}
                            onChange={(e) => setDriverSearch(e.target.value)}
                            className="w-full"
                          />
                        </div>
                        <div className="flex items-center gap-2 px-2 py-1 text-xs text-textMuted border-b border-card-border flex-wrap">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); selectAllDrivers(); }}
                            className="hover:text-primary"
                          >
                            Select all
                          </button>
                          <span>|</span>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); clearDriverSelection(); }}
                            className="hover:text-primary"
                          >
                            Deselect all
                          </button>
                        </div>
                        <div className="overflow-y-auto flex-1 min-h-0 py-1">
                          {(() => {
                            const filtered = drivers.filter(
                              (d) =>
                                !driverSearch.trim() ||
                                (d.driver_name || '')
                                  .toLowerCase()
                                  .includes(driverSearch.toLowerCase().trim())
                            );
                            if (filtered.length === 0) {
                              return (
                                <p className="px-3 py-2 text-xs text-textMuted">
                                  {driverSearch.trim() ? 'No drivers match.' : 'No drivers.'}
                                </p>
                              );
                            }
                            return filtered.map((driver) => {
                              const id = String(driver.driver_id);
                              const checked = selectedDriverIds.has(id);
                              return (
                                <label
                                  key={driver.driver_id}
                                  className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface cursor-pointer text-sm text-textPrimary"
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleDriver(driver.driver_id)}
                                    className=""
                                  />
                                  <span className="truncate flex-1 min-w-0">{driver.driver_name || id}</span>
                                  <DriverFreezeTypeBadge freezeType={driver.freeze_type} />
                                </label>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    </>
                  )}
                </div>
                <div className="relative flex-1 min-w-[min(200px,100%)] max-w-md">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-1.5 flex items-center pointer-events-none">
                      <svg className="h-3.5 w-3.5 shrink-0 text-main-light" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      placeholder="Search by driver, client, trip ID, address, time, LOS or space type..."
                      value={bucketTripSearch}
                      onChange={(e) => setBucketTripSearch(e.target.value)}
                      className="w-full rounded-md border border-gray-200 bg-white pl-7 pr-7 !py-1.5 !text-xs leading-tight text-main-secondary shadow-sm placeholder:text-main-light focus:border-main-primary focus:outline-none focus:ring-1 focus:ring-main-primary"
                      aria-label="Search by driver name, or unassigned and assigned trip details including time"
                    />
                    <button
                      type="button"
                      onClick={() => setBucketTripSearch('')}
                      className="absolute inset-y-0 right-0 flex w-7 items-center justify-center text-main-light hover:text-main-secondary"
                      style={{ visibility: bucketTripSearch ? 'visible' : 'hidden' }}
                      aria-label="Clear search"
                    >
                      <svg className="h-3.5 w-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  {searchTokensFromInput(debouncedBucketTripSearch).length > 0 && (
                    <p className="text-[10px] text-main-light mt-0.5 leading-tight">
                      Found {bucketTripMatchCount ?? 0} trip{bucketTripMatchCount !== 1 ? 's' : ''} in view
                    </p>
                  )}
                </div>
                {selectedDriverIds.size > 0 && (
                  <span className="text-xs text-textMuted">
                    Showing {driversToShow.length} of {drivers.length} driver columns
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => setTempTripFeasibilityOpen(true)}
                  className="ml-auto flex shrink-0 items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-textPrimary hover:bg-gray-50"
                >
                  <span>Trip Feasibility</span>
                  <svg className="h-3.5 w-3.5 shrink-0 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>

              <div className="flex flex-1 min-h-[400px] gap-0 overflow-hidden rounded-lg border border-card-border bg-surface shadow-sm items-stretch">
                {/* Unassigned sidebar */}
                <div className="w-[300px] flex-shrink-0 min-h-0 h-full overflow-hidden flex flex-col self-stretch">
                  <UnassignedSidebar
                    trips={unassignedTrips}
                    timezone={companyTimezone}
                    spaceTypeMap={assignmentData.metadata?.load_unload_times || {}}
                    tripSearch={debouncedBucketTripSearch}
                  />
                </div>

                {/* Horizontal scroll: Driver columns (only selected when filter set) */}
                <div className="flex flex-1 min-h-0 h-full flex-col self-stretch overflow-x-auto overflow-y-hidden p-1.5">
                  <div className="flex h-full min-h-0 items-stretch gap-1.5">
                    {driversToShow.length === 0 ? (
                      <div className="flex items-center justify-center flex-1 text-sm text-textMuted px-4 text-center">
                        {drivers.length === 0
                          ? 'No drivers'
                          : searchTokensFromInput(debouncedBucketTripSearch).length > 0 &&
                            driversAfterSelection.length > 0
                          ? 'No driver buckets match this search.'
                          : selectedDriverIds.size === 0
                          ? 'No drivers'
                          : 'No drivers selected. Clear filter or select drivers above.'}
                      </div>
                    ) : (
                      driversToShow.map((driver) => (
                        <DriverColumn
                          key={driver.driver_id}
                          driver={driver}
                          timezone={companyTimezone}
                          spaceTypeMap={assignmentData.metadata?.load_unload_times || {}}
                          multiloadRoutes={assignmentData?.multiload_routes || {}}
                          onExportLoad={
                            assignmentData && adminModeUnlocked && publishSelectionUnlocked
                              ? () => startDriverExport(driver)
                              : undefined
                          }
                          exportDisabled={!canExportDriver(driver)}
                          exportWorking={String(exportInProgressId) === String(resolvePlannerDriverIdForPortal(driver) || driver.driver_id)}
                          bucketTripSearch={debouncedBucketTripSearch}
                        />
                      ))
                    )}
                  </div>
                </div>
              </div>
              </div>
            </div>

            <DragOverlay dropAnimation={null}>
              {activeMultiloadTrips?.length ? (
                <div className="w-[280px] rounded-lg border-2 border-purple-400 bg-purple-50 dark:bg-purple-900/30 shadow-2xl ring-4 ring-purple-300/30 p-3">
                  <div className="text-sm font-bold text-purple-800 dark:text-purple-200">
                    Multiload group ({activeMultiloadTrips.length} trip{activeMultiloadTrips.length !== 1 ? 's' : ''})
                  </div>
                  <p className="text-xs text-purple-600 dark:text-purple-300 mt-1">Drag to unassigned or another driver</p>
                </div>
              ) : activeTrip ? (
                <div className="w-[280px] rounded-lg border-2 border-primary bg-card-bg shadow-2xl ring-4 ring-primary/20">
                  <TripCardContent
                    trip={activeTrip}
                    timezone={companyTimezone}
                    isUnassigned={activeIsUnassigned}
                    spaceTypeMap={assignmentData.metadata?.load_unload_times || {}}
                    compact
                  />
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>

      {exportError && (
        <>
          <div className="fixed inset-0 z-[100] bg-black/50" onClick={() => setExportError(null)} role="presentation" />
          <div className="fixed top-1/2 left-1/2 z-[110] w-full max-w-md -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl border border-red-200 bg-white shadow-2xl">
            <div className="border-b border-red-100 bg-gradient-to-r from-red-50 to-white px-6 py-4">
              <h3 className="text-lg font-bold text-red-700">Export failed</h3>
            </div>
            <div className="px-6 py-6">
              <p className="text-sm text-textPrimary">{exportError}</p>
            </div>
            <div className="flex justify-end border-t border-card-border bg-gray-50 px-6 py-4">
              <button type="button" onClick={() => setExportError(null)} className="rounded-md bg-main-primary px-4 py-2 text-sm font-medium text-white hover:bg-main-primary/90">
                OK
              </button>
            </div>
          </div>
        </>
      )}

      {driverExport && driverExport.step === 1 && (
        <>
          <div className="fixed inset-0 z-[100] bg-black/50" onClick={handleCloseDriverExport} role="presentation" />
          <div className="fixed top-1/2 left-1/2 z-[110] w-full max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
            <div className="border-b border-gray-200 bg-gradient-to-r from-slate-50 to-slate-100 px-6 py-4">
              <div className="flex items-start justify-between gap-3">
                <h3 className="text-lg font-bold text-textPrimary">Publish this driver</h3>
                <button type="button" onClick={handleCloseDriverExport} disabled={!!exportInProgressId} className="rounded-lg p-2 text-textMuted hover:bg-white/80 hover:text-textPrimary disabled:opacity-50" aria-label="Close">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-sm text-textPrimary">
                Do you want to publish the load for the driver <strong>{driverExport.driver.driver_name || driverExport.driver.driver_id}</strong>?
              </p>
            </div>
            <div className="flex justify-between border-t border-card-border bg-gray-50 px-6 py-4">
              <button
                type="button"
                onClick={handleCloseDriverExport}
                disabled={!!exportInProgressId}
                className="text-sm text-textMuted hover:text-textPrimary disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDriverExportStep1}
                disabled={!!exportInProgressId}
                className="inline-flex items-center gap-2 rounded-md bg-main-primary px-4 py-2 text-sm font-medium text-white hover:bg-main-primary/90 disabled:opacity-50"
              >
                {exportInProgressId && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
                {exportInProgressId ? 'Validating…' : 'Continue'}
              </button>
            </div>
          </div>
        </>
      )}

      {driverExport && driverExport.step === 2 && (
        <>
          <div className="fixed inset-0 z-[100] bg-black/50" onClick={handleCloseDriverExport} role="presentation" />
          <div className="fixed top-1/2 left-1/2 z-[110] w-full max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
            <div className="border-b border-gray-200 bg-gradient-to-r from-slate-50 to-slate-100 px-6 py-4">
              <div className="flex items-start justify-between gap-3">
                <h3 className="text-lg font-bold text-textPrimary">Confirm publish</h3>
                <button
                  type="button"
                  onClick={handleCloseDriverExport}
                  disabled={!!exportInProgressId}
                  className="rounded-lg p-2 text-textMuted hover:bg-white/80 hover:text-textPrimary disabled:opacity-50"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-sm text-textPrimary">
                <strong>{driverExport.validTripCount}</strong> trip{driverExport.validTripCount !== 1 ? 's' : ''} passed
                validation. Publish <strong>{driverExport.driver.driver_name || driverExport.driver.driver_id}</strong>?
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-card-border bg-gray-50 px-6 py-4">
              <button
                type="button"
                onClick={handleDriverExportBackToStep1}
                disabled={!!exportInProgressId}
                className="inline-flex items-center gap-1 text-sm text-textMuted hover:text-textPrimary disabled:opacity-50"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back
              </button>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleCloseDriverExport}
                  disabled={!!exportInProgressId}
                  className="rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-textPrimary hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDriverExportStep2}
                  disabled={!!exportInProgressId}
                  className="inline-flex items-center gap-2 rounded-md bg-main-primary px-4 py-2 text-sm font-medium text-white hover:bg-main-primary/90 disabled:opacity-50"
                >
                  {exportInProgressId && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
                  {exportInProgressId ? 'Publishing…' : 'Publish'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
